import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../features/auth/context';
import { useToast } from '../hooks/useToast';
import {
  calculateCarbonEmissions,
  CarbonAccountingInput,
  EmissionsResult,
  LatLng,
  TransportMode,
} from '../features/admin/carbonAccounting/api';
import CitySearchInput from '../features/geocoding/CitySearchInput';
import JourneyMap from '../features/maps/JourneyMap';
import Button from '../components/ui/button';
import Input from '../components/ui/Input';
import Select from '../components/ui/Select';
import FormField from '../components/ui/FormField';

// ─────────────────────────────────────────────
// Static data
// ─────────────────────────────────────────────

const PRESET_CITIES: Record<string, LatLng> = {
  Berlin: { lat: 52.52, lng: 13.405 },
  Munich: { lat: 48.135, lng: 11.582 },
  Frankfurt: { lat: 50.111, lng: 8.682 },
  Amsterdam: { lat: 52.368, lng: 4.904 },
  Barcelona: { lat: 41.385, lng: 2.173 },
  Lisbon: { lat: 38.722, lng: -9.139 },
  Paris: { lat: 48.857, lng: 2.353 },
  'New York': { lat: 40.713, lng: -74.006 },
};

const MODES: TransportMode[] = ['train', 'bus', 'car', 'airplane', 'motorcycle', 'van', 'ferry'];

// Mode-specific option fields, rendered conditionally below.
interface OptionFieldsProps {
  mode: TransportMode;
  options: Record<string, unknown>;
  setOptions: (next: Record<string, unknown>) => void;
}

const ModeOptionFields: React.FC<OptionFieldsProps> = ({ mode, options, setOptions }) => {
  const set = (key: string, value: unknown) => setOptions({ ...options, [key]: value });

  if (mode === 'train') {
    return (
      <FormField label="Train type">
        <Select
          value={(options.type as string) ?? 'electric'}
          onChange={(e) => set('type', e.target.value)}
        >
          <option value="electric">Electric (default)</option>
          <option value="diesel">Diesel</option>
        </Select>
      </FormField>
    );
  }

  if (mode === 'bus') {
    return (
      <FormField label="Bus type">
        <Select
          value={(options.type as string) ?? 'urban'}
          onChange={(e) => set('type', e.target.value)}
        >
          <option value="urban">Urban (default)</option>
          <option value="coach">Coach (long-distance)</option>
        </Select>
      </FormField>
    );
  }

  if (mode === 'car' || mode === 'van' || mode === 'motorcycle') {
    const fuelOptions =
      mode === 'car'
        ? ['diesel', 'petrol', 'hybrid', 'electric']
        : mode === 'van'
        ? ['diesel']
        : ['petrol'];
    return (
      <>
        <FormField label="Fuel">
          <Select
            value={(options.fuel as string) ?? fuelOptions[0]}
            onChange={(e) => set('fuel', e.target.value)}
          >
            {fuelOptions.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </Select>
        </FormField>
        <FormField label="Occupancy" hint="(persons in vehicle, default 1)">
          <Input
            type="number"
            min={1}
            value={(options.occupancy as number) ?? 1}
            onChange={(e) => set('occupancy', Math.max(1, Number(e.target.value)))}
          />
        </FormField>
      </>
    );
  }

  if (mode === 'ferry') {
    return (
      <FormField label="Passenger type">
        <Select
          value={(options.passengerType as string) ?? 'average'}
          onChange={(e) => set('passengerType', e.target.value)}
        >
          <option value="average">Average (default)</option>
          <option value="foot">Foot passenger</option>
          <option value="carPassenger">Car passenger</option>
        </Select>
      </FormField>
    );
  }

  if (mode === 'airplane') {
    return (
      <FormField label="Cabin">
        <Select
          value={(options.cabin as string) ?? 'economy'}
          onChange={(e) => set('cabin', e.target.value)}
        >
          <option value="economy">Economy (default)</option>
          <option value="business">Business</option>
        </Select>
      </FormField>
    );
  }

  return null;
};

// ─────────────────────────────────────────────
// Coordinate input — preset dropdown · search · manual override
//
// The Search mode wraps the shared <CitySearchInput> from features/geocoding.
// ─────────────────────────────────────────────

type InputMode = 'preset' | 'search' | 'manual';

interface CoordinateInputProps {
  label: string;
  value: LatLng;
  onChange: (next: LatLng) => void;
}

const CoordinateInput: React.FC<CoordinateInputProps> = ({ label, value, onChange }) => {
  const presetMatch =
    Object.entries(PRESET_CITIES).find(
      ([, coords]) => coords.lat === value.lat && coords.lng === value.lng
    )?.[0] ?? '';

  const [mode, setMode] = useState<InputMode>(presetMatch ? 'preset' : 'manual');
  const [searchLabel, setSearchLabel] = useState<string>('');

  const tabs: { key: InputMode; label: string }[] = [
    { key: 'preset', label: 'Preset' },
    { key: 'search', label: 'Search' },
    { key: 'manual', label: 'Manual' },
  ];

  return (
    <div style={{ border: '1px solid var(--border, #e2e8f0)', padding: 12, borderRadius: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <div style={{ fontWeight: 600 }}>{label}</div>
        <div
          role="tablist"
          aria-label={`${label} input mode`}
          style={{
            display: 'inline-flex',
            border: '1px solid #e2e8f0',
            borderRadius: 6,
            overflow: 'hidden',
            fontSize: 12,
          }}
        >
          {tabs.map((t) => (
            <button
              key={t.key}
              role="tab"
              type="button"
              aria-selected={mode === t.key}
              onClick={() => setMode(t.key)}
              style={{
                padding: '4px 10px',
                background: mode === t.key ? '#0f172a' : '#fff',
                color: mode === t.key ? '#fff' : '#0f172a',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {mode === 'preset' && (
        <FormField label="Preset city">
          <Select
            value={presetMatch}
            onChange={(e) => {
              const city = PRESET_CITIES[e.target.value];
              if (city) onChange(city);
            }}
          >
            <option value="">(none)</option>
            {Object.keys(PRESET_CITIES).map((city) => (
              <option key={city} value={city}>
                {city}
              </option>
            ))}
          </Select>
        </FormField>
      )}

      {mode === 'search' && (
        <>
          <FormField label="Search any city">
            <CitySearchInput
              initialLabel={searchLabel}
              onSelect={(city) => {
                if (city) {
                  setSearchLabel(city.displayName);
                  onChange({ lat: city.lat, lng: city.lng });
                } else {
                  setSearchLabel('');
                }
              }}
            />
          </FormField>
          {searchLabel && (
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
              Selected: <strong>{searchLabel}</strong> ({value.lat.toFixed(3)}, {value.lng.toFixed(3)})
            </div>
          )}
        </>
      )}

      {mode === 'manual' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <FormField label="Latitude">
            <Input
              type="number"
              step="any"
              value={value.lat}
              onChange={(e) => onChange({ ...value, lat: Number(e.target.value) })}
            />
          </FormField>
          <FormField label="Longitude">
            <Input
              type="number"
              step="any"
              value={value.lng}
              onChange={(e) => onChange({ ...value, lng: Number(e.target.value) })}
            />
          </FormField>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────
// Result card
// ─────────────────────────────────────────────

const ResultCard: React.FC<{
  title: string;
  emphasis?: 'primary' | 'secondary';
  co2eKg: number;
  distanceKm: number;
  explanation: string;
}> = ({ title, emphasis = 'secondary', co2eKg, distanceKm, explanation }) => {
  const isPrimary = emphasis === 'primary';
  return (
    <div
      style={{
        border: `1px solid ${isPrimary ? '#10b981' : '#e2e8f0'}`,
        background: isPrimary ? '#ecfdf5' : '#fff',
        borderRadius: 12,
        padding: 16,
      }}
    >
      <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.6, color: '#64748b' }}>
        {title}
      </div>
      <div style={{ fontSize: 32, fontWeight: 700, marginTop: 4 }}>
        {co2eKg.toLocaleString('en-US', { maximumFractionDigits: 2 })}{' '}
        <span style={{ fontSize: 16, fontWeight: 500, color: '#64748b' }}>kg CO₂e</span>
      </div>
      <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
        Distance used: {distanceKm.toLocaleString('en-US', { maximumFractionDigits: 1 })} km
      </div>
      <p style={{ marginTop: 12, fontSize: 14, lineHeight: 1.5 }}>{explanation}</p>
    </div>
  );
};

// ─────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────

const CarbonAccountingTestPage: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [from, setFrom] = useState<LatLng>(PRESET_CITIES.Berlin);
  const [to, setTo] = useState<LatLng>(PRESET_CITIES.Munich);
  const [mode, setMode] = useState<TransportMode>('airplane');
  const [options, setOptions] = useState<Record<string, unknown>>({});
  const [result, setResult] = useState<EmissionsResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [showJson, setShowJson] = useState(false);

  // Reset options when mode changes (avoids stale option keys)
  React.useEffect(() => {
    setOptions({});
  }, [mode]);

  // Block non-admins
  React.useEffect(() => {
    if (!authLoading && (!user || !user.isAdmin)) {
      showToast({ title: 'Access Denied', message: 'Admin access required.', type: 'error' });
      navigate('/');
    }
  }, [authLoading, user, navigate, showToast]);

  const handleCalculate = async () => {
    setLoading(true);
    setResult(null);
    try {
      const input: CarbonAccountingInput = {
        from,
        to,
        mode,
        options,
      };
      const data = await calculateCarbonEmissions(input);
      setResult(data);
    } catch (e: any) {
      const errorMessage = e?.response?.data?.error || e.message || 'Calculation failed.';
      showToast({ title: 'Error', message: errorMessage, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const requestPreview = useMemo(
    () => JSON.stringify({ from, to, mode, options }, null, 2),
    [from, to, mode, options]
  );

  if (authLoading || !user || !user.isAdmin) return null;

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700 }}>Carbon Accounting: Test Page</h1>
        <Button variant="ghost" onClick={() => navigate('/admin')}>← Back to Admin</Button>
      </div>

      <p style={{ color: '#475569', marginBottom: 16, fontSize: 14 }}>
        Self-contained engine for estimating CO₂e emissions of a single transport segment between
        two coordinates. The simplified result uses straight-line distance only; the comprehensive
        result adjusts distance for real-world routing and adds radiative forcing for flights.
      </p>

      <div style={{ marginBottom: 16 }}>
        <JourneyMap from={from} to={to} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <CoordinateInput label="From" value={from} onChange={setFrom} />
        <CoordinateInput label="To" value={to} onChange={setTo} />
      </div>

      <div
        style={{
          marginTop: 16,
          border: '1px solid var(--border, #e2e8f0)',
          padding: 12,
          borderRadius: 8,
        }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <FormField label="Transport mode">
            <Select value={mode} onChange={(e) => setMode(e.target.value as TransportMode)}>
              {MODES.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </Select>
          </FormField>
          <ModeOptionFields mode={mode} options={options} setOptions={setOptions} />
        </div>
      </div>

      <div style={{ marginTop: 16, display: 'flex', gap: 12, alignItems: 'center' }}>
        <Button onClick={handleCalculate} disabled={loading}>
          {loading ? 'Calculating…' : 'Calculate'}
        </Button>
      </div>

      {result && (
        <div style={{ marginTop: 24 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <ResultCard
              title="Simplified (straight line, CO₂ only)"
              emphasis="secondary"
              co2eKg={result.simplified.co2eKg}
              distanceKm={result.simplified.distanceKm}
              explanation={result.simplified.explanation}
            />
            <ResultCard
              title="Comprehensive (detour + RF)"
              emphasis="primary"
              co2eKg={result.comprehensive.co2eKg}
              distanceKm={result.comprehensive.distanceKm}
              explanation={result.comprehensive.explanation}
            />
          </div>

          <div style={{ marginTop: 16, fontSize: 13, color: '#64748b' }}>
            <strong>Factor used:</strong> {result.factor.value} {result.factor.unit} —{' '}
            {result.factor.source}
            {result.factor.flightCategory && (
              <> · flight category: <strong>{result.factor.flightCategory}</strong></>
            )}
          </div>

          <div style={{ marginTop: 16 }}>
            <Button variant="ghost" onClick={() => setShowJson((v) => !v)}>
              {showJson ? 'Hide' : 'Show'} raw response
            </Button>
            {showJson && (
              <pre
                style={{
                  background: '#0f172a',
                  color: '#e2e8f0',
                  padding: 12,
                  borderRadius: 8,
                  marginTop: 8,
                  fontSize: 12,
                  overflowX: 'auto',
                }}
              >
                {JSON.stringify(result, null, 2)}
              </pre>
            )}
          </div>
        </div>
      )}

      <details style={{ marginTop: 24 }}>
        <summary style={{ cursor: 'pointer', fontSize: 13, color: '#64748b' }}>
          Show request that will be sent
        </summary>
        <pre
          style={{
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            padding: 12,
            borderRadius: 8,
            marginTop: 8,
            fontSize: 12,
            overflowX: 'auto',
          }}
        >
          {requestPreview}
        </pre>
      </details>
    </div>
  );
};

export default CarbonAccountingTestPage;
